import { ApiAuth, ApiCall } from "./api";
import { TPremiseResponse } from "./interface/IDefaults";
import {
  IBulkCredentialPostActionsRequest,
  IBulkCredentialPostActionsResponse,
  IBulkCredentialsRequest,
  IBulkResponse,
} from "./interface/bulk.interface";
import {
  ECredentialType,
  IConfirmAutoConfigInput,
  IConfirmAutoConfigResponse,
  ICredential,
  ICredentialFilterOptionsResponse,
  ICredentialList,
  ICredentialPfxValidateResponse,
  ICreateCredentialInput,
  IEligibleProduct,
  IFilterListCredential,
  IGovbrConnectionAttempt,
  IGovbrSessionStatusItem,
  IUpdateCredentialInput,
} from "./interface/credential.interface";

export const listCredentials = async (
  guid_client: string,
  filters: IFilterListCredential
): Promise<TPremiseResponse<ICredentialList>> => {
  const url = `/credentials/${guid_client}/list`;
  return await new ApiCall(url, ApiAuth()).post<ICredentialList, IFilterListCredential>(filters);
};

export const getCredential = async (
  guid_client: string,
  guid_credential: string
): Promise<TPremiseResponse<ICredential>> => {
  return await new ApiCall(`/credentials/${guid_client}/${guid_credential}`, ApiAuth()).get<ICredential>();
};

export const createCredential = async (
  guid_client: string,
  type: ECredentialType,
  formData: FormData
): Promise<TPremiseResponse<ICredential>> => {
  return await new ApiCall(`/credentials/${guid_client}/${type}`, ApiAuth()).post<ICredential, FormData>(formData);
};

export interface ICreateSecretCredentialInput {
  // Titular: exatamente um de guid_company (empresa — login_password) OU
  // guid_persona (pessoa física / representante — gov.br).
  guid_company?: string;
  guid_persona?: string;
  /** GEN-3888 — titular pessoa por nome + CPF, sem pessoa física cadastrada. */
  titular_name?: string;
  titular_document?: string;
  description?: string;
  /**
   * GEN-3746 — como a credencial gov.br autentica.
   *
   * Ausente (o caso de sempre) = login e senha, e `secret` é obrigatório.
   * `"digital_certificate"` = o certificado custodiado da empresa titular É a credencial, e
   * então NÃO há `secret` a enviar. O backend grava isto no mesmo envelope selado do segredo,
   * sem coluna nova, e a automação lê dali para escolher o caminho de login.
   */
  auth_method?: "digital_certificate";
  /** Obrigatório quando não há `auth_method` — o DTO do backend exige login e senha não vazios. */
  secret?: {
    login: string;
    password: string;
    has_2fa?: boolean;
    twofa_method?: string;
  };
  consent: {
    accepted: boolean;
    terms_version?: string;
  };
  companies?: string[];
  companyIntegrations?: { guid_company: string; slug: string; params?: Record<string, string> }[];
  // GEN-3520: fluxo que oferece a consulta no FINAL (vitrine de elegíveis) cadastra sem disparar
  // a pesquisa dos produtos vinculados — mesmo contrato que o ramo multipart já enviava (GEN-3190 R4).
  deferProductIssue?: boolean;
}

/**
 * Cadastro de credencial baseada em segredo (gov.br / login-senha). Diferente do
 * fluxo de arquivo, envia JSON (não multipart): a senha trafega apenas aqui, é
 * encriptada via KMS no back-end e NUNCA é retornada por leitura. Mesmo endpoint
 * de criação; o back-end aceita application/json para os tipos de segredo.
 */
export const createSecretCredential = async (
  guid_client: string,
  type: ECredentialType,
  data: ICreateSecretCredentialInput
): Promise<TPremiseResponse<ICredential>> => {
  return await new ApiCall(`/credentials/${guid_client}/${type}`, ApiAuth()).post<
    ICredential,
    ICreateSecretCredentialInput
  >(data);
};

export const updateCredential = async (
  guid_client: string,
  guid_credential: string,
  data: IUpdateCredentialInput
): Promise<TPremiseResponse<ICredential>> => {
  return await new ApiCall(`/credentials/${guid_client}/${guid_credential}`, ApiAuth()).put<
    ICredential,
    IUpdateCredentialInput
  >(data);
};

/**
 * Ativar/desativar credencial. Rota específica de status — no back exige a permissão
 * `credential.delete` (o update genérico acima exige apenas `credential.write`).
 */
export const toggleCredentialStatus = async (
  guid_client: string,
  guid_credential: string,
  isActive: boolean
): Promise<TPremiseResponse<ICredential>> => {
  return await new ApiCall(`/credentials/${guid_client}/${guid_credential}/status`, ApiAuth()).patch<
    ICredential,
    { isActive: boolean }
  >({ isActive });
};

export const validateCredentialPfx = async (
  guid_client: string,
  formData: FormData
): Promise<TPremiseResponse<ICredentialPfxValidateResponse>> => {
  return await new ApiCall(`/credentials/${guid_client}/validate-pfx`, ApiAuth()).post<
    ICredentialPfxValidateResponse,
    FormData
  >(formData);
};

export const getCredentialFilterOptions = async (
  guid_client: string
): Promise<TPremiseResponse<ICredentialFilterOptionsResponse>> => {
  const url = `/filter/${guid_client}/credentials/0`;
  return await new ApiCall(url, ApiAuth()).get<ICredentialFilterOptionsResponse>();
};

export const getEligibleProducts = async (guid_client: string): Promise<TPremiseResponse<IEligibleProduct[]>> => {
  return await new ApiCall(`/credentials/${guid_client}/eligible-products`, ApiAuth()).get<IEligibleProduct[]>();
};

export const getAllCredentialProducts = async (guid_client: string): Promise<TPremiseResponse<IEligibleProduct[]>> => {
  return await new ApiCall(`/credentials/${guid_client}/all-products`, ApiAuth()).get<IEligibleProduct[]>();
};

export const renewCredential = async (
  guid_client: string,
  guid_credential: string,
  formData: FormData
): Promise<TPremiseResponse<ICredential>> => {
  return await new ApiCall(`/credentials/${guid_client}/${guid_credential}/renew`, ApiAuth()).put<
    ICredential,
    FormData
  >(formData);
};

/**
 * GEN-3221 — cadastro em lote de credenciais (preview/apply), no contrato comum de lote da casa.
 *
 * ⚠️ A senha só pode ir no `mode: 'apply'`. Quem monta o payload de preview precisa removê-la
 * (ver `stripSecrets` em BulkCredentialImport) — o servidor também a ignora nesse modo, mas não
 * enviá-la mantém o segredo trafegando uma vez só.
 */
/**
 * Teto de espera de um bloco do lote. O default do `ApiAuth` é de 20 minutos — pensado para
 * download/relatório —, mas aqui o wizard fica no passo de execução com o fechamento BLOQUEADO
 * enquanto a requisição não volta. Um bloco travado prenderia o usuário no modal por 20 minutos.
 * Um bloco de 25 credenciais (com KMS) roda em segundos; 3 minutos é folga generosa.
 */
const BULK_CREDENTIAL_TIMEOUT_MS = 180000;

export const postBulkCredentials = async (
  guid_client: string,
  body: IBulkCredentialsRequest
): Promise<TPremiseResponse<IBulkResponse>> => {
  return await new ApiCall(`/credentials/${guid_client}/bulk`, {
    ...ApiAuth(),
    timeout: BULK_CREDENTIAL_TIMEOUT_MS,
  }).post<IBulkResponse, IBulkCredentialsRequest>(body);
};

/**
 * GEN-3221 (r6) — efeitos PÓS-cadastro do lote: ativar recorrência e/ou pesquisar agora as
 * consultas das credenciais que o `apply` acabou de gravar.
 *
 * É uma requisição PRÓPRIA, disparada na etapa final do wizard, em vez de viajar dentro do apply.
 * Pedido do Thiago: separa a criação das credenciais da pesquisa em lote + configuração de
 * recorrência, e deixa o processamento mais leve.
 */
export const postBulkCredentialPostActions = async (
  guid_client: string,
  body: IBulkCredentialPostActionsRequest
): Promise<TPremiseResponse<IBulkCredentialPostActionsResponse>> => {
  return await new ApiCall(`/credentials/${guid_client}/bulk/post-actions`, {
    ...ApiAuth(),
    timeout: BULK_CREDENTIAL_TIMEOUT_MS,
  }).post<IBulkCredentialPostActionsResponse, IBulkCredentialPostActionsRequest>(body);
};

export const confirmCredentialAutoConfig = async (
  guid_client: string,
  guid_credential: string,
  data: IConfirmAutoConfigInput
): Promise<TPremiseResponse<IConfirmAutoConfigResponse>> => {
  return await new ApiCall(`/credentials/${guid_client}/${guid_credential}/auto-config`, ApiAuth()).post<
    IConfirmAutoConfigResponse,
    IConfirmAutoConfigInput
  >(data);
};

/**
 * GEN-3107 — estado das sessões gov.br de um lote de credenciais.
 *
 * Em lote porque a tela lista dezenas de linhas: uma chamada por credencial viraria rajada a
 * cada carregamento.
 *
 * Credencial que nunca conectou volta `NONE`, distinto de `EXPIRED` — "nunca conectou" e "a
 * conexão caiu" pedem textos e urgências diferentes ao cliente.
 */
export const getGovbrSessionStatus = async (
  guid_client: string,
  guids: string[]
): Promise<TPremiseResponse<Record<string, IGovbrSessionStatusItem>>> => {
  const url = `/credentials/${guid_client}/govbr-session/status`;
  return await new ApiCall(url, ApiAuth()).post<Record<string, IGovbrSessionStatusItem>, { guids: string[] }>({
    guids,
  });
};

/**
 * GEN-3754 — conecta a conta gov.br EM SEGUNDO PLANO. Responde na hora; o login roda no servidor
 * e o andamento sai por `getGovbrConnections` (o ícone da barra superior).
 *
 * Pedido repetido da mesma credencial devolve a conexão que já está em curso (`reused`): um
 * clique a mais não vira outro login real na conta do titular.
 */
export const connectGovbrCredential = async (
  guid_client: string,
  guid_credential: string
): Promise<TPremiseResponse<{ attempt: IGovbrConnectionAttempt; reused: boolean }>> => {
  const url = `/credentials/${guid_client}/govbr-session/${guid_credential}/connect`;
  return await new ApiCall(url, ApiAuth()).post<{ attempt: IGovbrConnectionAttempt; reused: boolean }, undefined>(
    undefined
  );
};

/** GEN-3754 — conexões do usuário logado: em curso, erro não visto e sucesso recente. */
export const getGovbrConnections = async (
  guid_client: string
): Promise<TPremiseResponse<IGovbrConnectionAttempt[]>> => {
  const url = `/credentials/${guid_client}/govbr-session/connections`;
  return await new ApiCall(url, ApiAuth()).get<IGovbrConnectionAttempt[]>();
};

/** GEN-3754 — a pessoa viu o desfecho: o badge de erro apaga. */
export const markGovbrConnectionSeen = async (
  guid_client: string,
  id: number
): Promise<TPremiseResponse<IGovbrConnectionAttempt>> => {
  const url = `/credentials/${guid_client}/govbr-session/connections/${id}/seen`;
  return await new ApiCall(url, ApiAuth()).post<IGovbrConnectionAttempt, undefined>(undefined);
};

/** GEN-3754 — print da tela em que o login parou (data URL). ⚠️ Contém dados do titular. */
export const getGovbrConnectionScreenshot = async (
  guid_client: string,
  id: number
): Promise<TPremiseResponse<{ data_url: string }>> => {
  const url = `/credentials/${guid_client}/govbr-session/connections/${id}/screenshot`;
  return await new ApiCall(url, ApiAuth()).get<{ data_url: string }>();
};
